import { readonly, ref } from 'vue';

/**
 * Cookie management utility with SSR safety
 */

export interface CookieOptions {
  domain?: string;
  expiryDays?: number;
  path?: string;
  sameSite?: 'Lax' | 'None' | 'Strict';
  secure?: boolean;
}

const cookieStore = ref<Record<string, string>>({});

export function useCookies() {
  const get = (name: string) => {
    // SSR safety check
    if (typeof document === 'undefined') {
      return null;
    }

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      if (cookieValue) {
        cookieStore.value[name] = decodeURIComponent(cookieValue);
        return decodeURIComponent(cookieValue);
      }
    }

    return null;
  };

  const set = (name: string, value: string, options: CookieOptions = {}): void => {
    // SSR safety check
    if (typeof document === 'undefined') {
      return;
    }

    const { domain = '', expiryDays = 365, path = '/', sameSite = 'Lax', secure = true } = options;

    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expiryDays);

    const cookieParts = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expiryDate.toUTCString()}`,
      `path=${path}`,
      `SameSite=${sameSite}`,
    ];

    if (domain) {
      cookieParts.push(`domain=${domain}`);
    }

    if (secure) {
      cookieParts.push('secure');
    }

    document.cookie = cookieParts.join('; ');
    cookieStore.value[name] = value;
  };

  const remove = (name: string, options: Pick<CookieOptions, 'domain' | 'path'> = {}): void => {
    set(name, '', { ...options, expiryDays: -1 });
    delete cookieStore.value[name];
  };

  const has = (name: string): boolean => {
    return get(name) !== null;
  };

  const getAll = (): Record<string, string> => {
    // SSR safety check
    if (typeof document === 'undefined') {
      return {};
    }

    const cookies: Record<string, string> = {};
    const cookieArray = document.cookie.split(';');

    for (const cookie of cookieArray) {
      const [name, value] = cookie.trim().split('=');
      if (name && value) {
        cookies[name] = decodeURIComponent(value);
      }
    }

    cookieStore.value = { ...cookies };
    return cookies;
  };

  const clear = (): void => {
    const allCookies = getAll();
    for (const name in allCookies) {
      remove(name);
    }
  };

  return {
    clear,
    cookies: readonly(cookieStore),
    get,
    getAll,
    has,
    remove,
    set,
  };
}
